import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession, PullIndicator, usePullToRefresh } from 'gunsi-auth'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import { makeMemberLabel, youOf } from '../util/you.js'
import { memberShareOf, paidAmountOf, expensePayers } from '../util/balances.js'
import { fmtMoney, fmtDate } from '../format.js'

export function Activity() {
  const t = useEvenI18n()
  const navigate = useNavigate()
  const { session } = useSession()
  const { groups, expenses, settlements, loading, refresh, dateFormat } = useEven()
  const { listRef, pull, refreshing, ready } = usePullToRefresh(refresh)

  const byGroup = useMemo(() => Object.fromEntries(groups.map(g => [g.id, g])), [groups])

  const items = useMemo(() => {
    const a = expenses.map(e => ({ ...e, _type: 'expense' }))
    const b = settlements.map(s => ({ ...s, _type: 'settlement' }))
    return [...a, ...b].sort((x, y) => (y.createdAt ?? y.date ?? '').localeCompare(x.createdAt ?? x.date ?? ''))
  }, [expenses, settlements])

  if (loading) {
    return <div className="loading-screen"><div className="loading-spinner" /></div>
  }

  return (
    <main className="page page-activity">
      <header className="page-head">
        <h1>{t.activity.title}</h1>
      </header>

      <div className="ptr-wrap">
        <PullIndicator pull={pull} refreshing={refreshing} ready={ready} />
        <ul
          className="activity-list"
          ref={listRef}
          style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: pull ? undefined : 'transform 180ms ease-out' }}
        >
          {items.length === 0 && (
            <li className="empty-state"><p>{t.activity.empty}</p></li>
          )}
          {items.map(item => {
            const g = byGroup[item.groupId]
            if (!g) return null
            const memberLabel = makeMemberLabel(g)
            const you = youOf(g, session?.user?.id)
            const cur = item.currency || g.currency

            // Impatto personale: cosa cambia per te con questo movimento
            let impact = null
            if (you) {
              if (item._type === 'expense') {
                // Netto personale: quanto ho anticipato meno la mia quota
                // (regge anche il multi-pagante: 30 pagati su quota 25 → +5)
                const net = Math.round((paidAmountOf(item, you.id) - memberShareOf(item, you.id)) * 100) / 100
                if (net > 0.004) impact = { kind: 'lent', text: `${t.activity.youLent} ${fmtMoney(net, cur)}` }
                else if (net < -0.004) impact = { kind: 'owe', text: `${t.groups.youOwe} ${fmtMoney(-net, cur)}` }
              } else {
                if (item.to === you.id) impact = { kind: 'lent', text: `${t.activity.youReceived} ${fmtMoney(item.amount, cur)}` }
                else if (item.from === you.id) impact = { kind: 'muted', text: `${t.activity.youSettled} ${fmtMoney(item.amount, cur)}` }
              }
            }

            return (
              <li key={`${item._type}-${item.id}`} className="activity-row" onClick={() => navigate(`/groups/${g.id}`)}>
                <div className="activity-emoji">
                  {item._type === 'expense' ? (item.emoji || '💸') : '≈'}
                </div>
                <div className="activity-meta">
                  <div className="activity-title">
                    {item._type === 'expense' ? (
                      <>
                        <strong>
                          {expensePayers(item).length > 1
                            ? t.activity.nPeople.replace('{n}', String(expensePayers(item).length))
                            : memberLabel(expensePayers(item)[0]?.memberId)}
                        </strong>
                        {' '}{t.activity.paidFor}{' '}
                        <strong>{item.description || t.activity.expense}</strong>
                      </>
                    ) : (
                      <><strong>{memberLabel(item.from)}</strong> → <strong>{memberLabel(item.to)}</strong></>
                    )}
                  </div>
                  {impact && (
                    <div className={`act-impact ${impact.kind}`}>{impact.text}</div>
                  )}
                  <div className="activity-sub">
                    <span className="group-tag">{g.emoji || '👥'} {g.name}</span>
                    {' · '}{fmtDate(item.date, undefined, dateFormat)}
                  </div>
                </div>
                <div className={`activity-amount${item._type === 'settlement' ? ' muted' : ''}`}>
                  {fmtMoney(item.amount, cur)}
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </main>
  )
}
